/**
 * web/src/components/shared/StatusBadge.tsx
 * Maps WorkflowStatus → Badge variant + human label. Lifted verbatim from
 * planning-docs/design_handoff_pixel_dashboard/design_files/shell.jsx
 * STATUS_META (lines 137-144).
 *
 * Note: server has no synthetic 'rejected' workflow status — that lives on
 * the *step*. WorkflowList derives "rejected mode" by checking
 * status === 'paused' && hasRejectedStep (CONTEXT.md D-4.4 + PATTERNS.md
 * line 1155). This component renders by what's passed in; callers that
 * have already detected the rejected case pass `'rejected'` explicitly.
 *
 * Threat T-05-04-01: the `??` fallback to `cancelled` ensures unknown
 * status values render safely instead of crashing the workflow card.
 */
import { Badge } from './Badge';
import type { StepStatus, WorkflowStatus } from '../../api/types';

/** Status keys understood by StatusBadge. `awaiting_reply` is a synthetic key
 * (the workflow is paused on an automated step waiting for an external email
 * reply) — callers pass it explicitly; the server has no such workflow status. */
export type StatusKey = WorkflowStatus | 'rejected' | 'awaiting_reply';

/** Variant strings reused across the status palette. */
type StatusVariant =
  | 'running'
  | 'waiting'
  | 'awaiting'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'default';

/** Exhaustive status → label/variant map. `paused` (a human gate — needs our
 * team) and `awaiting_reply` (a sponsor/external reply — needs them) are
 * deliberately given DIFFERENT variants/colors so they don't blend together
 * in the list + detail views: amber = our action, teal = waiting on them. */
const STATUS_META: Record<StatusKey, { label: string; variant: StatusVariant }> = {
  pending: { label: 'Pending', variant: 'default' },
  running: { label: 'Running', variant: 'running' },
  paused: { label: 'Waiting approval', variant: 'waiting' },
  rejected: { label: 'Rejected', variant: 'failed' },
  awaiting_reply: { label: 'Awaiting reply', variant: 'awaiting' },
  completed: { label: 'Completed', variant: 'completed' },
  failed: { label: 'Failed', variant: 'failed' },
  cancelled: { label: 'Cancelled', variant: 'cancelled' },
};

/**
 * Resolves the badge key for a workflow row. A paused workflow's top-level
 * status doesn't say WHY it's paused, so refine it from the current step's
 * status: a rejected gate → 'rejected', an automated step waiting on an
 * external email reply (waiting_event) → 'awaiting_reply'. Everything else
 * (including a genuine gate awaiting approval) falls through to the workflow
 * status. Undefined currentStepStatus (older cached rows) → no refinement.
 */
export function deriveWorkflowStatusKey(w: {
  status: WorkflowStatus;
  currentStepStatus?: StepStatus | null;
}): StatusKey {
  if (w.status === 'paused') {
    if (w.currentStepStatus === 'rejected') return 'rejected';
    if (w.currentStepStatus === 'waiting_event') return 'awaiting_reply';
  }
  return w.status;
}

/**
 * Renders a Badge tagged with the standardized status label + variant.
 * @param status WorkflowStatus, 'rejected', or 'awaiting_reply'
 */
export function StatusBadge({ status }: { status: StatusKey }) {
  const meta = STATUS_META[status] ?? STATUS_META.cancelled;
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}
